import { useState, useEffect } from "react";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import SequenceConfirmationPopup from "../BillingEvents/SequenceConfirmationPopup";

/**
 * SequenceButton Component
 * Displays a button to manually sequence billing events
 * Shows count of pending events and triggers sequencing popup on click
 * 
 * @param {number} reservationId - ID of reservation (if sequencing reservation events)
 * @param {number} customOrderId - ID of custom order (if sequencing custom order event)
 * @param {number} contactId - ID of contact/customer (if sequencing all customer custom orders)
 * @param {number} companyId - ID of company (if sequencing all company events)
 * @param {function} onSequenced - Callback fired after successful sequencing
 * @param {number} refreshTrigger - Optional number that changes to trigger a refresh
 * @param {function} handleSubmit - Optional function to save form before sequencing
 */
const SequenceButton = ({ reservationId, customOrderId, contactId, companyId, onSequenced, refreshTrigger, handleSubmit }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [sequencing, setSequencing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Determine which endpoint to call based on props
  const getEndpoint = () => {
    if (reservationId) {
      return `/api/billing-events/pending-count/reservation/${reservationId}`;
    } else if (customOrderId) {
      return `/api/billing-events/pending-count/custom-order/${customOrderId}`;
    } else if (contactId) {
      return `/api/billing-events/pending-count/customer/${contactId}`;
    } else if (companyId) {
      return `/api/billing-events/pending-count/company/${companyId}`;
    }
    return null;
  };

  const getSequenceEndpoint = () => {
    if (reservationId) {
      return `/api/billing-events/sequence-reservation/${reservationId}`;
    } else if (customOrderId) {
      return `/api/billing-events/sequence-custom-order/${customOrderId}`;
    } else if (contactId) {
      return `/api/billing-events/sequence-customer/${contactId}`;
    } else if (companyId) {
      return `/api/companies/${companyId}/sequence`; // Using existing company endpoint
    }
    return null;
  };

  // Fetch pending count on mount and when refreshTrigger changes
  useEffect(() => {
    fetchPendingCount();
  }, [reservationId, customOrderId, contactId, companyId, refreshTrigger]);

  const fetchPendingCount = async () => {
    setLoading(true);
    const endpoint = getEndpoint();

    if (!endpoint) {
      setLoading(false);
      return { count: 0, events: [] };
    }

    try {
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getCookie("_AT")}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.success) {
        setPendingCount(result.count || 0);
        setPendingEvents(result.events || []);
        return { count: result.count || 0, events: result.events || [] };
      } else {
        setPendingCount(0);
        setPendingEvents([]);
        return { count: 0, events: [] };
      }
    } catch (error) {
      console.error("Error fetching pending billing events count:", error);
      setPendingCount(0);
      setPendingEvents([]);
      return { count: 0, events: [] };
    } finally {
      setLoading(false);
    }
  };

  const handleSequence = async (businessDateISO, selectedBranchCodes, _companies, excludedEventIds) => {
    setSequencing(true);
    const endpoint = getSequenceEndpoint();

    if (!endpoint) {
      setSequencing(false);
      return;
    }

    // businessDateISO is already converted to UTC ISO string by the popup using branch timezone
    const finalBusinessDate = businessDateISO || new Date().toISOString();

    try {
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getCookie("_AT")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          businessDate: finalBusinessDate,
          selectedBranchCodes: selectedBranchCodes || [],
          excludedEventIds: excludedEventIds || []
        }),
      });

      const result = await response.json();

      // Handle the case where the company sequencing returns { processed, details }
      // instead of standard { success, message, sequencedCount }
      // We normalize it here
      const isSuccess = result.success !== undefined ? result.success : (result.processed !== undefined);
      const processedCount = result.sequencedCount || result.processed || 0;

      if (isSuccess) {
        // Success - close popup and refresh
        setShowPopup(false);
        setSubmitError(null);
        setPendingCount(0);
        setPendingEvents([]);

        // Trigger callback to refresh parent component
        if (onSequenced && typeof onSequenced === "function") {
          onSequenced(result);
        }
      } else {
        setSubmitError(result.message || "Failed to sequence billing events");
      }
    } catch (error) {
      console.error("Error sequencing billing events:", error);
      setSubmitError("Failed to sequence billing events. Please try again.");
    } finally {
      setSequencing(false);
    }
  };

  // Handle button click: save form, refresh count, then show popup
  const handleButtonClick = async () => {
    setSaving(true);

    try {
      // Save form if handleSubmit is provided
      if (handleSubmit && typeof handleSubmit === "function") {
        await handleSubmit();
      }

      // Refresh pending count to get latest data
      const { count, events } = await fetchPendingCount();

      // Only show popup if there are events to sequence
      if (count > 0 && events.length > 0) {
        setShowPopup(true);
      }
    } catch (error) {
      console.error("Error preparing for sequencing:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading || saving) {
    return (
      <button
        disabled
        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-400 bg-gray-50 cursor-not-allowed"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="animate-spin h-4 w-4 mr-2"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        {saving ? "Bill" : "Bill"}
      </button>
    );
  }

  const hasEvents = pendingCount > 0;

  return (
    <>
      <button
        onClick={handleButtonClick}
        className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md transition-colors ${hasEvents
            ? "border-indigo-600 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            : "border-gray-400 text-gray-600 bg-gray-50 hover:bg-gray-100 hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          }`}
      >
        {hasEvents ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5" />
            </svg>
            Bill [{pendingCount}]
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Bill
          </>
        )}
      </button>

      {showPopup && (
        <SequenceConfirmationPopup
          events={pendingEvents}
          onConfirm={handleSequence}
          onCancel={() => {
            setShowPopup(false);
            setSubmitError(null);
          }}
          sequencing={sequencing}
          isCompanyBilling={!!companyId}
          submitError={submitError}
        />
      )}
    </>
  );
};


export default SequenceButton;
